/**
 * Admin-skal: sidebar med admin-faner på desktop, Sheet-menu på mobil.
 * Superadmin-only faner (Mail, Indstillinger) skjules for sælgere.
 */
import { useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft,
  BadgePercent,
  Banknote,
  Building2,
  Coins,
  FileText,
  Image,
  Images,
  LayoutDashboard,
  LineChart,
  Lock,
  Mail,
  Menu,
  Package,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Tags,
  UserPlus,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/features/auth/AuthContext";
import { ImpersonationBanner } from "@/features/sales/components/ImpersonationBanner";
import { cn } from "@/lib/utils";

interface AdminTab {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  superAdminOnly?: boolean;
}

const TABS: AdminTab[] = [
  { to: "/admin", label: "Overblik", icon: LayoutDashboard, end: true },
  { to: "/admin/anmodninger", label: "Anmodninger", icon: UserPlus },
  { to: "/admin/statistik", label: "Statistik", icon: LineChart },
  { to: "/admin/provision", label: "Provision", icon: Banknote },
  { to: "/admin/firmaer", label: "Firmaer", icon: Building2 },
  { to: "/admin/brugere", label: "Brugere", icon: Users },
  { to: "/admin/prispakker", label: "Prispakker", icon: Package },
  { to: "/admin/priser", label: "Priser", icon: Tags },
  { to: "/admin/produktadgang", label: "Produktadgang", icon: Lock },
  { to: "/admin/cashback", label: "Cashback", icon: Coins },
  { to: "/admin/kampagner", label: "Kampagner", icon: Image },
  { to: "/admin/kategori-billeder", label: "Kategori-billeder", icon: Images },
  { to: "/admin/ordrer", label: "Ordrer", icon: ShoppingCart },
  { to: "/admin/fakturaer", label: "Fakturaer", icon: FileText },
  { to: "/admin/mail", label: "Mail & notifikationer", icon: Mail, superAdminOnly: true },
  { to: "/admin/indstillinger", label: "Indstillinger", icon: Settings, superAdminOnly: true },
];

function TabLink({ tab, onNavigate }: { tab: AdminTab; onNavigate?: () => void }) {
  const Icon = tab.icon;
  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isActive ? "accent-soft text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      {tab.label}
    </NavLink>
  );
}

export function AdminLayout() {
  const { isSuperAdmin } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const tabs = TABS.filter((t) => !t.superAdminOnly || isSuperAdmin);
  const activeTab = tabs.find((t) => (t.end ? location.pathname === t.to : location.pathname.startsWith(t.to)));

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <ImpersonationBanner />

      {/* Mobil topbar */}
      <header className="sticky top-0 z-30 flex items-center gap-2 border-b bg-background px-3 pb-2 pt-[calc(env(safe-area-inset-top)+0.5rem)] md:hidden">
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="h-11 w-11" aria-label="Åbn admin-menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0">
            <SheetHeader className="border-b p-4 text-left">
              <SheetTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
                Administration
              </SheetTitle>
            </SheetHeader>
            <ScrollArea className="h-[calc(100dvh-4.5rem)]">
              <nav className="flex flex-col gap-1 p-3" aria-label="Admin-navigation">
                {tabs.map((tab) => (
                  <TabLink key={tab.to} tab={tab} onNavigate={() => setMenuOpen(false)} />
                ))}
                <Link
                  to="/dashboard"
                  onClick={() => setMenuOpen(false)}
                  className="mt-2 flex min-h-11 items-center gap-3 rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Tilbage til portalen
                </Link>
              </nav>
            </ScrollArea>
          </SheetContent>
        </Sheet>
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Administration</p>
          <h1 className="truncate text-base font-semibold">{activeTab?.label ?? "Admin"}</h1>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <aside className="hidden w-64 shrink-0 border-r bg-card md:block" aria-label="Admin-navigation">
          <div className="sticky top-0 flex h-dvh flex-col">
            <div className="flex items-center gap-2 border-b p-4">
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
              <span className="font-mono text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Administration</span>
            </div>
            <ScrollArea className="flex-1">
              <nav className="flex flex-col gap-1 p-3">
                {tabs.map((tab) => (
                  <TabLink key={tab.to} tab={tab} />
                ))}
              </nav>
            </ScrollArea>
            <div className="border-t p-3">
              <Link
                to="/dashboard"
                className="flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
                Tilbage til portalen
              </Link>
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 p-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
